import { useState } from "react";
import { Wallet, Eye, EyeOff, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function ResetPassword() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);

  const resetMutation = trpc.auth.local.resetPassword.useMutation({
    onSuccess: () => setDone(true),
    onError: (err) => {
      setFailed(true);
      toast.error(err.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || !confirm) return toast.error("Preencha todos os campos");
    if (newPassword !== confirm) return toast.error("As senhas não coincidem");
    if (newPassword.length < 6) return toast.error("A senha deve ter pelo menos 6 caracteres");
    if (!token) return toast.error("Token de redefinição inválido ou ausente");
    resetMutation.mutate({ token, newPassword });
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mb-3 shadow-lg">
            <Wallet className="h-7 w-7 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Bolso
          </h1>
        </div>

        <Card className="shadow-md border-border/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-xl">Redefinir senha</CardTitle>
            <CardDescription>Escolha uma nova senha para sua conta</CardDescription>
          </CardHeader>
          <CardContent>
            {!token ? (
              <div className="text-center py-4">
                <XCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-4">
                  Link de redefinição inválido ou expirado. Solicite um novo link na tela de login.
                </p>
                <Button variant="outline" className="w-full" onClick={() => navigate("/")}>
                  Voltar ao login
                </Button>
              </div>
            ) : done ? (
              <div className="text-center py-4">
                <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-4">
                  Senha redefinida com sucesso! Agora você pode fazer login com sua nova senha.
                </p>
                <Button className="w-full" onClick={() => navigate("/")}>
                  Ir para o login
                </Button>
              </div>
            ) : failed ? (
              <div className="text-center py-4">
                <XCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-4">
                  O link de redefinição é inválido ou expirou. Solicite um novo link.
                </p>
                <Button variant="outline" className="w-full" onClick={() => navigate("/")}>
                  Voltar ao login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="new-password">Nova senha</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPw ? "text" : "password"}
                      placeholder="Mínimo 6 caracteres"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password">Confirmar nova senha</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Repita a nova senha"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={resetMutation.isPending}>
                  {resetMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Redefinir senha
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
